// ============================================================
//  src/pages/bots/tabs/PanelTab.js
//  Panel = the bot's own settings: API keys, phone numbers, prices,
//  messages… Each field has a key the bot reads as {{panel.key}} in
//  commands and $panel['key'] in endpoint code.
//  Styles: ./PanelTab.css (scoped under .pnl-*)
// ============================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getPanel, savePanelField, updatePanelValue, deletePanelField } from '../../../api/client';
import toast from 'react-hot-toast';
import './PanelTab.css';

const TYPES = [
  { v: 'text',     icon: 'fa-font',          label: 'Text',      desc: 'A short line — names, IDs, account numbers' },
  { v: 'textarea', icon: 'fa-align-left',    label: 'Long text', desc: 'Messages and paragraphs, keeps line breaks' },
  { v: 'number',   icon: 'fa-hashtag',       label: 'Number',    desc: 'Prices, limits, percentages' },
  { v: 'password', icon: 'fa-key',           label: 'Secret',    desc: 'API keys and tokens — hidden on screen' },
  { v: 'url',      icon: 'fa-link',          label: 'Link',      desc: 'A web address starting with https://' },
  { v: 'email',    icon: 'fa-at',            label: 'Email',     desc: 'An email address' },
  { v: 'select',   icon: 'fa-list-ul',       label: 'Choice',    desc: 'One value from a set you decide' },
];
const typeMeta = (t) => TYPES.find(x => x.v === t) ?? TYPES[0];

const PRESETS = [
  { pTitle: 'API key',          pKey: 'api_key',         pType: 'password', pIsSecret: 1, pIcon: '🔑', pDescription: 'Key from your VTU / payment provider. Used by commands that call their API.' },
  { pTitle: 'Support phone',    pKey: 'support_phone',   pType: 'text',     pIcon: '📞', pDescription: 'Number customers can call or WhatsApp for help.' },
  { pTitle: 'Welcome message',  pKey: 'welcome_message', pType: 'textarea', pIcon: '👋', pDescription: 'First message new customers see.' },
  { pTitle: 'Bank account',     pKey: 'bank_account',    pType: 'text',     pIcon: '🏦', pDescription: 'Account customers pay into, e.g. 0123456789 · GTBank · PrimeBiller Ltd' },
  { pTitle: 'Service charge',   pKey: 'service_charge',  pType: 'number',   pIcon: '💰', pDescription: 'Amount in naira added to each order.' },
  { pTitle: 'Website',          pKey: 'website_url',     pType: 'url',      pIcon: '🌐', pDescription: 'Your website or payment page.' },
];

const isSecretField = (f) => Number(f.pIsSecret) === 1 || f.pType === 'password';
const isLegacyFlow = (f) => String(f.pKey).startsWith('flow_json_');
const slugKey = (s) => String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);

function copy(text, label = 'Copied') {
  navigator.clipboard?.writeText(text).then(() => toast.success(label)).catch(() => {});
}

function useBodyLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
}

function Spinner({ dark }) { return <span className={`pnl-spin ${dark ? 'is-dark' : ''}`} />; }

function FieldIcon({ icon, type }) {
  const i = String(icon ?? '').trim();
  if (i.startsWith('fa-')) return <span className="pnl-fic"><i className={`fa-solid ${i}`} /></span>;
  if (i) return <span className="pnl-fic is-emoji">{i}</span>;
  return <span className="pnl-fic"><i className={`fa-solid ${typeMeta(type).icon}`} /></span>;
}

function Sheet({ icon, tone = 'brand', title, sub, onClose, children, footer, wide }) {
  useBodyLock();
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="pnl-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`pnl-sheet ${wide ? 'is-wide' : ''}`} role="dialog" aria-label={title}>
        <span className="pnl-grip" />
        <div className="pnl-sheet-head">
          <span className={`pnl-sheet-ic is-${tone}`}><i className={`fa-solid ${icon}`} /></span>
          <div className="pnl-sheet-title"><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
          <button type="button" className="pnl-icon-btn" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="pnl-sheet-body">{children}</div>
        {footer && <div className="pnl-sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** The value input, shaped by the field type. Shared by the card and the editor preview. */
function ValueInput({ field, value, onChange, onEnter, onEscape, autoFocus, id }) {
  const [shown, setShown] = useState(false);
  const secret = isSecretField(field);
  const placeholder = field.pType === 'url' ? 'https://…' : field.pType === 'email' ? 'name@example.com'
    : field.pType === 'number' ? '0' : `Enter ${String(field.pTitle || 'a value').toLowerCase()}`;
  const keys = (e) => {
    if (e.key === 'Enter' && (field.pType !== 'textarea' || e.metaKey || e.ctrlKey)) { e.preventDefault(); onEnter?.(); }
    if (e.key === 'Escape') onEscape?.();
  };
  if (field.pType === 'textarea') {
    return <textarea id={id} className="pnl-input is-area" value={value} onChange={e => onChange(e.target.value)} onKeyDown={keys}
      placeholder={placeholder} rows={Math.min(8, Math.max(3, String(value).split('\n').length + 1))} autoFocus={autoFocus} />;
  }
  return (
    <div className="pnl-input-wrap">
      <input id={id} className={`pnl-input ${secret ? 'is-mono' : ''}`} value={value} onChange={e => onChange(e.target.value)} onKeyDown={keys}
        type={secret && !shown ? 'password' : field.pType === 'number' ? 'number' : field.pType === 'email' ? 'email' : field.pType === 'url' ? 'url' : 'text'}
        inputMode={field.pType === 'number' ? 'decimal' : undefined} placeholder={placeholder} autoFocus={autoFocus}
        autoComplete={secret ? 'new-password' : 'off'} spellCheck={!secret && field.pType === 'text'} />
      {secret && (
        <button type="button" className="pnl-input-btn" onClick={() => setShown(s => !s)} aria-label={shown ? 'Hide value' : 'Show value'} title={shown ? 'Hide' : 'Show'}>
          <i className={`fa-solid ${shown ? 'fa-eye-slash' : 'fa-eye'}`} />
        </button>
      )}
    </div>
  );
}

function valueProblem(type, v) {
  const s = String(v ?? '').trim();
  if (!s) return null;
  if (type === 'url' && !/^https?:\/\/\S+\.\S+/i.test(s)) return 'This doesn’t look like a link — it should start with https://';
  if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return 'This doesn’t look like an email address';
  if (type === 'number' && Number.isNaN(Number(s))) return 'This should be a number';
  return null;
}

// ── Usage snippets ────────────────────────────────────────────
function UsageRow({ pKey }) {
  return (
    <div className="pnl-usage">
      <span className="pnl-usage-label"><i className="fa-solid fa-code" /> Use it</span>
      <button type="button" className="pnl-snip" onClick={() => copy(`{{panel.${pKey}}}`, 'Copied — paste it into a command')} title="For commands, automations and broadcasts">
        <em>Commands</em><code>{`{{panel.${pKey}}}`}</code><i className="fa-regular fa-copy" />
      </button>
      <button type="button" className="pnl-snip" onClick={() => copy(`$panel['${pKey}']`, 'Copied — paste it into endpoint code')} title="For custom endpoint PHP code">
        <em>Endpoints</em><code>{`$panel['${pKey}']`}</code><i className="fa-regular fa-copy" />
      </button>
    </div>
  );
}

// ── Field card ───────────────────────────────────────────────
function FieldCard({ field, draft, onDraft, onSave, saving, justSaved, onEdit, onDelete }) {
  const [showUse, setShowUse] = useState(false);
  const saved = String(field.pValue ?? '');
  const dirty = draft !== undefined && draft !== saved;
  const value = draft !== undefined ? draft : saved;
  const empty = saved.trim() === '';
  const secret = isSecretField(field);
  const problem = valueProblem(field.pType, value);
  const t = typeMeta(field.pType);
  const inputId = `pnl-v-${field.pId}`;

  return (
    <article className={`pnl-card ${dirty ? 'is-dirty' : ''} ${empty && !dirty ? 'is-empty' : ''}`}>
      <div className="pnl-card-head">
        <FieldIcon icon={field.pIcon} type={field.pType} />
        <div className="pnl-card-title">
          <label htmlFor={inputId}>{field.pTitle || field.pKey}</label>
          <div className="pnl-card-tags">
            <button type="button" className="pnl-keychip" onClick={() => copy(field.pKey, 'Key copied')} title="Copy key">
              {field.pKey}<i className="fa-regular fa-copy" />
            </button>
            {field.pType !== 'password' && <span className="pnl-tag"><i className={`fa-solid ${t.icon}`} /> {t.label}</span>}
            {secret && <span className="pnl-tag is-secret"><i className="fa-solid fa-lock" /> Secret</span>}
            {field.pTag && <span className="pnl-tag is-muted">{field.pTag}</span>}
          </div>
        </div>
        <span className={`pnl-state ${dirty ? 'is-dirty' : justSaved ? 'is-saved' : empty ? 'is-empty' : 'is-ok'}`}>
          {dirty ? <><span className="pnl-dot" /> Unsaved</> : justSaved ? <><i className="fa-solid fa-check" /> Saved</> : empty ? 'Needs a value' : <><i className="fa-solid fa-circle-check" /> Set</>}
        </span>
      </div>

      {field.pDescription && <p className="pnl-card-desc">{field.pDescription}</p>}

      <div className="pnl-card-value">
        <ValueInput id={inputId} field={field} value={value} onChange={v => onDraft(field.pKey, v)}
          onEnter={() => dirty && onSave(field)} onEscape={() => onDraft(field.pKey, undefined)} />
        <div className="pnl-card-actions">
          {dirty && (
            <button type="button" className="pnl-btn ghost sm" onClick={() => onDraft(field.pKey, undefined)} title="Undo changes (Esc)">
              <i className="fa-solid fa-rotate-left" /><span className="pnl-hide-sm">Undo</span>
            </button>
          )}
          <button type="button" className="pnl-btn primary sm" onClick={() => onSave(field)} disabled={!dirty || saving}>
            {saving ? <Spinner /> : <i className="fa-solid fa-check" />} Save
          </button>
        </div>
      </div>
      {problem && <p className="pnl-warn"><i className="fa-solid fa-triangle-exclamation" /> {problem}</p>}
      {secret && !empty && !dirty && <p className="pnl-note"><i className="fa-solid fa-shield-halved" /> Hidden on screen and left out of exports unless you choose to include it.</p>}

      <div className="pnl-card-foot">
        <button type="button" className={`pnl-link ${showUse ? 'is-on' : ''}`} onClick={() => setShowUse(s => !s)}>
          <i className="fa-solid fa-code" /> How to use this <i className={`fa-solid fa-chevron-${showUse ? 'up' : 'down'}`} />
        </button>
        <span className="pnl-grow" />
        <button type="button" className="pnl-icon-btn" onClick={() => onEdit(field)} title="Edit field settings" aria-label="Edit field settings"><i className="fa-solid fa-pen" /></button>
        <button type="button" className="pnl-icon-btn danger" onClick={() => onDelete(field)} title="Delete field" aria-label="Delete field"><i className="fa-regular fa-trash-can" /></button>
      </div>
      {showUse && <UsageRow pKey={field.pKey} />}
    </article>
  );
}

// ── Field editor ─────────────────────────────────────────────
function FieldModal({ bId, field, preset, existingKeys, onClose, onSaved }) {
  const isEdit = !!field;
  const base = field ?? preset ?? {};
  const [form, setForm] = useState({
    pKey:         base.pKey         ?? '',
    pTitle:       base.pTitle       ?? '',
    pDescription: base.pDescription ?? '',
    pType:        base.pType        ?? 'text',
    pTag:         base.pTag         ?? '',
    pIcon:        base.pIcon        ?? '',
    pIsSecret:    Number(base.pIsSecret ?? 0),
    pOrder:       base.pOrder       ?? 0,
    pValue:       base.pValue       ?? '',
  });
  const [keyTouched, setKeyTouched] = useState(isEdit || !!preset);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setTitle(v) {
    setForm(f => ({ ...f, pTitle: v, pKey: keyTouched ? f.pKey : slugKey(v) }));
  }
  function setType(v) {
    setForm(f => ({ ...f, pType: v, pIsSecret: v === 'password' ? 1 : f.pIsSecret }));
  }

  const keyErr = !form.pKey.trim() ? 'Give the field a key'
    : !/^[A-Za-z0-9_.-]+$/.test(form.pKey) ? 'Use letters, numbers and _ only (no spaces)'
    : !isEdit && existingKeys.includes(form.pKey.trim()) ? 'A field with this key already exists'
    : null;
  const titleErr = !form.pTitle.trim() ? 'Give the field a name' : null;

  async function handleSave() {
    setTried(true);
    if (titleErr || keyErr) return;
    setSaving(true);
    try {
      const res = await savePanelField({ bId, ...form, pKey: form.pKey.trim(), pOrder: Number(form.pOrder) || 0 });
      if (res.data?.success) {
        toast.success(isEdit ? 'Field updated' : 'Field added');
        onSaved();
      } else {
        toast.error(res.data?.message ?? 'Failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message ?? 'Network error');
    }
    setSaving(false);
  }

  const previewField = { ...form, pId: 'preview' };

  return (
    <Sheet icon={isEdit ? 'fa-pen' : 'fa-plus'} title={isEdit ? 'Edit field' : 'New panel field'} wide onClose={onClose}
      sub={isEdit ? <>Changing settings for <code>{field.pKey}</code></> : 'A setting your bot can read — like an API key, a phone number or a message'}
      footer={<>
        <button type="button" className="pnl-btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="pnl-btn primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner /> : <i className="fa-solid fa-check" />} {isEdit ? 'Save changes' : 'Add field'}
        </button>
      </>}>
      <div className="pnl-editor">
        <div className="pnl-editor-form">
          <div className="pnl-step"><span>1</span> Name it</div>
          <label className="pnl-field">
            <span className="pnl-label">Name</span>
            <input className={`pnl-input ${tried && titleErr ? 'is-bad' : ''}`} value={form.pTitle} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. PocketFi secret key" autoFocus={!isEdit} />
            <span className="pnl-hint">What you’ll see on this page.</span>
            {tried && titleErr && <span className="pnl-err">{titleErr}</span>}
          </label>
          <label className="pnl-field">
            <span className="pnl-label">Key <em>{isEdit ? 'can’t be changed' : 'used by your bot'}</em></span>
            <div className="pnl-key-input">
              <span>panel.</span>
              <input className={`pnl-input is-mono ${tried && keyErr ? 'is-bad' : ''}`} value={form.pKey} disabled={isEdit}
                onChange={e => { setKeyTouched(true); set('pKey', e.target.value.replace(/\s+/g, '_')); }} placeholder="PocketFi_secret_key" spellCheck={false} />
            </div>
            <span className="pnl-hint">
              {isEdit
                ? 'Commands and endpoints find this field by its key, so it stays the same. To rename it, add a new field and delete this one.'
                : <>Filled in from the name. Your commands will read it as <code>{`{{panel.${form.pKey || 'key'}}}`}</code>.</>}
            </span>
            {tried && keyErr && <span className="pnl-err">{keyErr}</span>}
          </label>
          <label className="pnl-field">
            <span className="pnl-label">Help text <em>optional</em></span>
            <input className="pnl-input" value={form.pDescription} onChange={e => set('pDescription', e.target.value)}
              placeholder="e.g. From PocketFi → Settings → API keys" />
            <span className="pnl-hint">Shown under the field so whoever fills it in knows what goes here.</span>
          </label>

          <div className="pnl-step"><span>2</span> Choose what it holds</div>
          <div className="pnl-types">
            {TYPES.map(t => (
              <button key={t.v} type="button" className={`pnl-type ${form.pType === t.v ? 'is-on' : ''}`} onClick={() => setType(t.v)}>
                <i className={`fa-solid ${t.icon}`} />
                <span><b>{t.label}</b><em>{t.desc}</em></span>
              </button>
            ))}
          </div>
          {form.pType === 'select' && (
            <p className="pnl-hint">Choice fields are filled in as text here — type the option you want the bot to use.</p>
          )}

          <label className={`pnl-toggle ${form.pIsSecret ? 'is-on' : ''}`}>
            <span className="pnl-toggle-ic"><i className={`fa-solid ${form.pIsSecret ? 'fa-lock' : 'fa-lock-open'}`} /></span>
            <span className="pnl-toggle-txt">
              <b>Secret</b>
              <em>Hides the value on screen and leaves it out of exports unless you choose to include it. Turn this on for API keys, tokens and PINs.</em>
            </span>
            <span className="pnl-switch">
              <input type="checkbox" checked={!!form.pIsSecret} disabled={form.pType === 'password'} onChange={e => set('pIsSecret', e.target.checked ? 1 : 0)} />
              <span />
            </span>
          </label>

          <div className="pnl-step"><span>3</span> Value</div>
          <div className="pnl-field">
            <span className="pnl-label">{isEdit ? 'Current value' : 'Starting value'} <em>optional</em></span>
            <ValueInput field={previewField} value={form.pValue} onChange={v => set('pValue', v)} />
            {valueProblem(form.pType, form.pValue) && <span className="pnl-err">{valueProblem(form.pType, form.pValue)}</span>}
          </div>

          <button type="button" className="pnl-link" onClick={() => setShowAdvanced(s => !s)}>
            <i className="fa-solid fa-sliders" /> Advanced <i className={`fa-solid fa-chevron-${showAdvanced ? 'up' : 'down'}`} />
          </button>
          {showAdvanced && (
            <div className="pnl-adv">
              <label className="pnl-field">
                <span className="pnl-label">Icon</span>
                <input className="pnl-input" value={form.pIcon} onChange={e => set('pIcon', e.target.value)} placeholder="🔑  or  fa-key" />
                <span className="pnl-hint">An emoji, or a Font Awesome name like fa-key.</span>
              </label>
              <label className="pnl-field">
                <span className="pnl-label">Position</span>
                <input className="pnl-input" type="number" value={form.pOrder} onChange={e => set('pOrder', e.target.value)} />
                <span className="pnl-hint">Lower numbers show first.</span>
              </label>
              <label className="pnl-field">
                <span className="pnl-label">Tag</span>
                <input className="pnl-input" value={form.pTag ?? ''} onChange={e => set('pTag', e.target.value)} placeholder="optional" />
                <span className="pnl-hint">A label for your own sorting. Imported bots sometimes set this.</span>
              </label>
            </div>
          )}
        </div>

        <aside className="pnl-editor-preview">
          <div className="pnl-preview-label"><i className="fa-regular fa-eye" /> Preview</div>
          <div className="pnl-preview-card">
            <div className="pnl-card-head">
              <FieldIcon icon={form.pIcon} type={form.pType} />
              <div className="pnl-card-title">
                <label>{form.pTitle || 'Field name'}</label>
                <div className="pnl-card-tags">
                  <span className="pnl-keychip is-static">{form.pKey || 'key'}</span>
                  {form.pIsSecret ? <span className="pnl-tag is-secret"><i className="fa-solid fa-lock" /> Secret</span> : null}
                </div>
              </div>
            </div>
            {form.pDescription && <p className="pnl-card-desc">{form.pDescription}</p>}
            <div className="pnl-preview-input">
              {form.pType === 'textarea'
                ? <div className="pnl-fake is-area">{form.pValue || 'Your text…'}</div>
                : <div className="pnl-fake">{form.pValue ? (form.pIsSecret ? '•'.repeat(Math.min(18, form.pValue.length)) : form.pValue) : typeMeta(form.pType).label + '…'}</div>}
            </div>
          </div>
          <div className="pnl-preview-label"><i className="fa-solid fa-code" /> In a command</div>
          <pre className="pnl-preview-code">{`{
  "type": "send_text",
  "text": "${form.pType === 'number' ? 'Fee: ₦' : ''}{{panel.${form.pKey || 'key'}}}"
}`}</pre>
          <p className="pnl-hint">The bot swaps <code>{`{{panel.${form.pKey || 'key'}}}`}</code> for the value when it runs.</p>
        </aside>
      </div>
    </Sheet>
  );
}

// ── Main tab ──────────────────────────────────────────────────
export default function PanelTab({ bId }) {
  const [fields, setFields]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // null | { field } | { preset } | {}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [drafts, setDrafts]   = useState({});     // pKey → unsaved value
  const [savingKeys, setSavingKeys] = useState({});
  const [justSaved, setJustSaved] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [q, setQ]             = useState('');
  const [filter, setFilter]   = useState('all'); // all | empty | secret
  const [showFlows, setShowFlows] = useState(false);
  const guideKey = `pnl-guide-hidden:${bId}`;
  const [showGuide, setShowGuide] = useState(() => { try { return localStorage.getItem(guideKey) !== '1'; } catch { return true; } });

  const load = useCallback(async () => {
    try {
      const res = await getPanel(bId);
      if (res.data?.success) setFields(res.data.fields ?? []);
      else toast.error(res.data?.message ?? 'Failed to load panel');
    } catch { toast.error('Failed to load panel'); }
    setLoading(false);
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  // Warn before leaving the page with unsaved values
  const dirtyKeys = useMemo(() => Object.keys(drafts).filter(k => {
    const f = fields.find(x => x.pKey === k);
    return f && drafts[k] !== String(f.pValue ?? '');
  }), [drafts, fields]);
  useEffect(() => {
    if (!dirtyKeys.length) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirtyKeys.length]);

  function setDraft(pKey, value) {
    setDrafts(d => {
      const n = { ...d };
      if (value === undefined) delete n[pKey]; else n[pKey] = value;
      return n;
    });
  }

  async function saveValue(field) {
    const value = drafts[field.pKey];
    if (value === undefined) return true;
    setSavingKeys(s => ({ ...s, [field.pKey]: true }));
    let ok = false;
    try {
      const res = await updatePanelValue({ bId: field.pBotId ?? bId, pKey: field.pKey, pValue: value });
      if (res.data?.success) {
        ok = true;
        setFields(fs => fs.map(f => f.pKey === field.pKey ? { ...f, pValue: value } : f));
        setDraft(field.pKey, undefined);
        setJustSaved(s => ({ ...s, [field.pKey]: true }));
        setTimeout(() => setJustSaved(s => { const n = { ...s }; delete n[field.pKey]; return n; }), 2200);
      } else toast.error(res.data?.message ?? `Couldn’t save ${field.pTitle || field.pKey}`);
    } catch { toast.error('Network error'); }
    setSavingKeys(s => { const n = { ...s }; delete n[field.pKey]; return n; });
    return ok;
  }

  async function saveOne(field) {
    if (await saveValue(field)) toast.success(`${field.pTitle || field.pKey} saved`);
  }

  async function saveAll() {
    setSavingAll(true);
    let n = 0;
    for (const k of dirtyKeys) {
      const f = fields.find(x => x.pKey === k);
      if (f && await saveValue(f)) n++;
    }
    setSavingAll(false);
    if (n) toast.success(`${n} value${n === 1 ? '' : 's'} saved`);
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await deletePanelField(bId, deleteTarget.pId);
      if (res.data?.success === false) toast.error(res.data?.message ?? 'Failed to delete');
      else {
        toast.success('Field deleted');
        setFields(prev => prev.filter(f => f.pId !== deleteTarget.pId));
        setDraft(deleteTarget.pKey, undefined);
      }
    } catch { toast.error('Failed to delete'); }
    setDeleting(false);
    setDeleteTarget(null);
  }

  function hideGuide(hide) {
    setShowGuide(!hide);
    try { localStorage.setItem(guideKey, hide ? '1' : '0'); } catch { /* storage unavailable */ }
  }

  const regular = fields.filter(f => !isLegacyFlow(f));
  const flowFields = fields.filter(isLegacyFlow);
  const counts = {
    all: regular.length,
    empty: regular.filter(f => String(f.pValue ?? '').trim() === '').length,
    secret: regular.filter(isSecretField).length,
  };
  const needle = q.trim().toLowerCase();
  const visible = regular.filter(f => {
    if (filter === 'empty' && String(f.pValue ?? '').trim() !== '') return false;
    if (filter === 'secret' && !isSecretField(f)) return false;
    if (!needle) return true;
    return `${f.pTitle} ${f.pKey} ${f.pDescription ?? ''} ${f.pTag ?? ''}`.toLowerCase().includes(needle);
  });

  const cardProps = (f) => ({
    field: f, draft: drafts[f.pKey], onDraft: setDraft, onSave: saveOne, saving: !!savingKeys[f.pKey] || savingAll,
    justSaved: !!justSaved[f.pKey], onEdit: (x) => setModal({ field: x }), onDelete: setDeleteTarget,
  });

  return (
    <div className="pnl">
      {/* ── Header ── */}
      <section className="pnl-hero">
        <div className="pnl-hero-copy">
          <span className="pnl-hero-ic"><i className="fa-solid fa-sliders" /></span>
          <div>
            <h2>Panel</h2>
            <p>Your bot’s settings in one place — change a value here and every command using it updates instantly, no code edits.</p>
          </div>
        </div>
        <div className="pnl-hero-actions">
          <button type="button" className="pnl-btn on-dark" onClick={() => hideGuide(showGuide)}>
            <i className="fa-regular fa-circle-question" /> <span>{showGuide ? 'Hide guide' : 'How it works'}</span>
          </button>
          <button type="button" className="pnl-btn primary" onClick={() => setModal({})}>
            <i className="fa-solid fa-plus" /> <span>Add field</span>
          </button>
        </div>
        {!loading && regular.length > 0 && (
          <div className="pnl-hero-stats">
            {[['all', 'fa-layer-group', 'Fields'], ['empty', 'fa-circle-exclamation', 'Need a value'], ['secret', 'fa-lock', 'Secrets']].map(([k, ic, l]) => (
              <button key={k} type="button" className={`pnl-hero-stat ${filter === k ? 'is-on' : ''} ${k === 'empty' && counts.empty ? 'is-warn' : ''}`} onClick={() => setFilter(k)}>
                <i className={`fa-solid ${ic}`} /><b>{counts[k]}</b><span>{l}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ── Guide ── */}
      {showGuide && (
        <section className="pnl-guide">
          <div className="pnl-guide-head">
            <h3><i className="fa-solid fa-lightbulb" /> How the Panel works</h3>
            <button type="button" className="pnl-icon-btn" onClick={() => hideGuide(true)} aria-label="Hide guide"><i className="fa-solid fa-xmark" /></button>
          </div>
          <div className="pnl-guide-steps">
            <div className="pnl-guide-step">
              <span className="pnl-guide-n">1</span>
              <b>Add a field</b>
              <p>Give it a name like <i>Support phone</i>. It gets a key — <code>support_phone</code> — that your bot uses to find it.</p>
            </div>
            <div className="pnl-guide-step">
              <span className="pnl-guide-n">2</span>
              <b>Fill in the value</b>
              <p>Type the real value, e.g. <i>0803 123 4567</i>, and press Save. Change it any time — nothing else to update.</p>
            </div>
            <div className="pnl-guide-step">
              <span className="pnl-guide-n">3</span>
              <b>Use it anywhere</b>
              <p>Write <code>{'{{panel.support_phone}}'}</code> in a command, automation or broadcast, or <code>{"$panel['support_phone']"}</code> in endpoint code.</p>
            </div>
          </div>
          <div className="pnl-guide-example">
            <div>
              <span className="pnl-guide-ex-label">You write</span>
              <div className="pnl-bubble is-code">Call us on {'{{panel.support_phone}}'}</div>
            </div>
            <i className="fa-solid fa-arrow-right" />
            <div>
              <span className="pnl-guide-ex-label">Customer sees</span>
              <div className="pnl-bubble">Call us on 0803 123 4567</div>
            </div>
          </div>
          <p className="pnl-guide-tip"><i className="fa-solid fa-lock" /><span>Put API keys and tokens here as <b>Secret</b> fields instead of typing them into commands — they stay hidden on screen and out of exports.</span></p>
        </section>
      )}

      {/* ── Toolbar ── */}
      {!loading && regular.length > 0 && (
        <div className="pnl-toolbar">
          <div className="pnl-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, key or help text…" aria-label="Search fields" />
            {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search"><i className="fa-solid fa-xmark" /></button>}
          </div>
          <div className="pnl-seg">
            {[['all', 'All'], ['empty', 'Need a value'], ['secret', 'Secrets']].map(([k, l]) => (
              <button key={k} type="button" className={filter === k ? 'is-on' : ''} onClick={() => setFilter(k)}>{l} <span>{counts[k]}</span></button>
            ))}
          </div>
        </div>
      )}

      {/* ── Fields ── */}
      {loading ? (
        <div className="pnl-grid">{[0, 1, 2, 3].map(i => <div key={i} className="pnl-skel"><span /><span /><span /></div>)}</div>
      ) : regular.length === 0 ? (
        <section className="pnl-empty">
          <span className="pnl-empty-ic"><i className="fa-solid fa-sliders" /></span>
          <h3>No panel fields yet</h3>
          <p>Start with one of these, or make your own. You can change everything later.</p>
          <div className="pnl-presets">
            {PRESETS.map(p => (
              <button key={p.pKey} type="button" className="pnl-preset" onClick={() => setModal({ preset: p })}>
                <span className="pnl-fic is-emoji">{p.pIcon}</span>
                <span><b>{p.pTitle}</b><em>{typeMeta(p.pType).label}{Number(p.pIsSecret) === 1 ? ' · secret' : ''}</em></span>
                <i className="fa-solid fa-plus" />
              </button>
            ))}
          </div>
          <button type="button" className="pnl-btn primary" onClick={() => setModal({})}><i className="fa-solid fa-plus" /> Add a blank field</button>
        </section>
      ) : visible.length === 0 ? (
        <div className="pnl-none">
          <i className="fa-solid fa-magnifying-glass" />
          <b>{filter === 'empty' && !needle ? 'Every field has a value 🎉' : 'No fields match'}</b>
          <button type="button" className="pnl-link" onClick={() => { setQ(''); setFilter('all'); }}>Show all fields</button>
        </div>
      ) : (
        <div className="pnl-grid">
          {visible.map(f => <FieldCard key={f.pId} {...cardProps(f)} />)}
        </div>
      )}

      {/* ── Legacy flow JSON stored in the panel ── */}
      {flowFields.length > 0 && (
        <section className="pnl-flows">
          <button type="button" className="pnl-flows-head" onClick={() => setShowFlows(s => !s)}>
            <span className="pnl-fic"><i className="fa-solid fa-table-list" /></span>
            <span><b>Flow JSON from an imported bot</b><em>{flowFields.length} field{flowFields.length === 1 ? '' : 's'} · usually managed from the Flows tab</em></span>
            <i className={`fa-solid fa-chevron-${showFlows ? 'up' : 'down'}`} />
          </button>
          {showFlows && <div className="pnl-grid is-one">{flowFields.map(f => <FieldCard key={f.pId} {...cardProps(f)} />)}</div>}
        </section>
      )}

      {/* ── Unsaved bar ── */}
      {dirtyKeys.length > 0 && (
        <div className="pnl-savebar" role="status">
          <span className="pnl-dot" />
          <span className="pnl-savebar-txt"><b>{dirtyKeys.length} unsaved change{dirtyKeys.length === 1 ? '' : 's'}</b><em className="pnl-hide-sm"> — your bot still uses the old value{dirtyKeys.length === 1 ? '' : 's'}</em></span>
          <button type="button" className="pnl-btn ghost-dark sm" onClick={() => setDrafts({})} disabled={savingAll}>Discard</button>
          <button type="button" className="pnl-btn primary sm" onClick={saveAll} disabled={savingAll}>
            {savingAll ? <Spinner /> : <i className="fa-solid fa-check" />} Save all
          </button>
        </div>
      )}

      {!loading && regular.length > 0 && (
        <button type="button" className="pnl-fab" onClick={() => setModal({})} aria-label="Add field"><i className="fa-solid fa-plus" /></button>
      )}

      {modal && (
        <FieldModal
          bId={bId}
          field={modal.field ?? null}
          preset={modal.preset ?? null}
          existingKeys={fields.map(f => f.pKey)}
          onClose={() => setModal(null)}
          onSaved={() => { if (modal.field) setDraft(modal.field.pKey, undefined); setModal(null); load(); }}
        />
      )}

      {deleteTarget && (
        <Sheet icon="fa-trash-can" tone="danger" title="Delete this field?" onClose={() => setDeleteTarget(null)}
          footer={<>
            <button type="button" className="pnl-btn ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
            <button type="button" className="pnl-btn danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner /> : <i className="fa-regular fa-trash-can" />} Delete field
            </button>
          </>}>
          <div className="pnl-del-key"><FieldIcon icon={deleteTarget.pIcon} type={deleteTarget.pType} /><div><b>{deleteTarget.pTitle || deleteTarget.pKey}</b><code>{deleteTarget.pKey}</code></div></div>
          <p className="pnl-muted">
            Any command, automation or endpoint that uses <code>{`{{panel.${deleteTarget.pKey}}}`}</code> will get an empty value instead.
            This can’t be undone.
          </p>
        </Sheet>
      )}
    </div>
  );
}